/**
 * Events data mapper - transforms API response to UI model
 */

import { getDictValue } from '@/lib/utils/dictMapper';

export interface EventImage {
  src: string;
  alt: string;
  placeholder: string;
}

export interface Event {
  id: number;
  title: string;
  image: EventImage;
  description: string;
  organization: string;
  date: string;
  location: string;
  category: string;
  status: string;
}

export interface NavigationConfig {
  showArrows: boolean;
  showDots: boolean;
  autoPlay: boolean;
  autoPlayInterval: number;
}

export interface LayoutConfig {
  itemsPerView: number;
  spacing: number;
  aspectRatio: string;
  cardStyle: string;
}

export interface CTAConfig {
  text: string;
  icon: string;
  variant: string;
  size: string;
}

export interface EventsModel {
  title: string;
  subtitle: string;
  badge: string;
  events: Event[];
  navigation: NavigationConfig;
  layout: LayoutConfig;
  cta: CTAConfig;
}

// Real backend API response structure
export interface BackendEventTiming {
  startDate: string;
  endDate: string;
  timezone: string;
}

export interface BackendEventAddress {
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
  fullAddress: string;
}

export interface BackendEventContactInfo {
  email: string;
  phone: string;
  website: string;
  address: BackendEventAddress;
  socialMedia: {
    facebook: string;
    twitter: string;
  };
}

export interface BackendEventCategory {
  _id: string;
  name: string;
  key: string;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  description?: string;
  showOnSite: boolean;
  image?: string;
}

export interface BackendEventOrganizer {
  _id: string;
  name: string;
  approvalStatus: string;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
  address?: BackendEventAddress;
  banner?: string;
  description?: string;
  email?: string;
  image?: string;
  phone?: string;
  website?: string;
  showOnSite: boolean;
}

export interface BackendEvent {
  id: string;
  name: string;
  place: string;
  timing: BackendEventTiming;
  contactInformations: BackendEventContactInfo;
  description: string;
  mainImage: string;
  categories: BackendEventCategory[];
  organiser: BackendEventOrganizer;
  rsvps: any[];
  eventFees: string;
  eventRegions: string;
  approvalStatus: string;
  isActive: boolean;
  isDeleted: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface BackendEventsResponse {
  success: boolean;
  statusCode: number;
  message: string;
  data: BackendEvent[];
  meta: {
    requestId: string;
    timestamp: string;
    duration: number;
    pagination: {
      page: string;
      limit: string;
      total: number;
      totalPages: number;
      hasNextPage: boolean;
      hasPrevPage: boolean;
    };
    path: string;
    method: string;
    userAgent: string;
    ip: string;
  };
}

// Legacy API response (for fallback)
export interface EventsApiResponse {
  title: string;
  subtitle: string;
  badge: string;
  events: Event[];
  navigation: NavigationConfig;
  layout: LayoutConfig;
  cta: CTAConfig;
}

/**
 * Maps backend API response to Events UI model
 */
export function mapEventsFromBackend(backendData: BackendEventsResponse, dict: any): EventsModel {
  // Filter active events that should be shown on site
  const activeEvents = backendData.data.filter(event => event.isActive && !event.isDeleted && event.approvalStatus === 'approved');
  
  // Map backend events to UI events
  const events: Event[] = activeEvents.map((backendEvent, index) => ({
    id: index + 1, // Use index as ID for UI
    title: backendEvent?.name,
    image: {
      src: backendEvent?.mainImage || 'https://socialconnection-prod-bucket.s3.us-east-1.amazonaws.com/events/event-image-1757615847161_1757615882504.png',
      alt: backendEvent?.name,
      placeholder: 'https://socialconnection-prod-bucket.s3.us-east-1.amazonaws.com/events/event-image-1757615847161_1757615882504.png'
    },
    description: backendEvent?.description || '',
    organization: backendEvent?.organiser?.name,
    date: formatEventDate(backendEvent.timing.startDate, backendEvent.timing.endDate),
    location: backendEvent?.contactInformations?.address?.fullAddress || backendEvent.place,
    category: backendEvent.categories.length > 0 ? backendEvent.categories[0].name : 'General',
    status: getEventStatus(backendEvent.timing.startDate, backendEvent.timing.endDate)
  }));

  return {
    title: getDictValue(dict, 'events_title'),
    subtitle: getDictValue(dict, 'events_subtitle'),
    badge: getDictValue(dict, 'events_badge'),
    events,
    navigation: {
      showArrows: true,
      showDots: true,
      autoPlay: false,
      autoPlayInterval: 5000
    },
    layout: {
      itemsPerView: 3,
      spacing: 16,
      aspectRatio: '16:9',
      cardStyle: 'modern'
    },
    cta: {
      text: getDictValue(dict, 'events_viewAll'),
      icon: 'arrow-right',
      variant: 'primary',
      size: 'md'
    }
  };
}

/**
 * Formats event date for display
 */
function formatEventDate(startDate: string, endDate: string): string {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  // Check if it's the same day
  if (start.toDateString() === end.toDateString()) {
    return start.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  }
  
  // Different days - show range
  const startFormatted = start.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric' 
  });
  const endFormatted = end.toLocaleDateString('en-US', { 
    month: 'short', 
    day: 'numeric',
    year: 'numeric'
  });
  
  return `${startFormatted} - ${endFormatted}`;
}

/**
 * Determines event status based on dates
 */
function getEventStatus(startDate: string, endDate: string): string {
  const now = new Date();
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  if (now < start) {
    return 'upcoming';
  } else if (now >= start && now <= end) {
    return 'ongoing';
  } else {
    return 'past';
  }
}

/**
 * Maps API response to Events UI model (legacy)
 */
export function mapEventsFromApi(apiData: EventsApiResponse): EventsModel {
  return {
    title: apiData.title,
    subtitle: apiData.subtitle,
    badge: apiData.badge,
    events: apiData.events.map(event => ({
      id: event.id,
      title: event.title,
      image: {
        src: event.image.src,
        alt: event.image.alt,
        placeholder: event.image.placeholder
      },
      description: event.description,
      organization: event.organization,
      date: event.date,
      location: event.location,
      category: event.category,
      status: event.status
    })),
    navigation: {
      showArrows: apiData.navigation.showArrows,
      showDots: apiData.navigation.showDots,
      autoPlay: apiData.navigation.autoPlay,
      autoPlayInterval: apiData.navigation.autoPlayInterval
    },
    layout: {
      itemsPerView: apiData.layout.itemsPerView,
      spacing: apiData.layout.spacing,
      aspectRatio: apiData.layout.aspectRatio,
      cardStyle: apiData.layout.cardStyle
    },
    cta: {
      text: apiData.cta.text,
      icon: apiData.cta.icon,
      variant: apiData.cta.variant,
      size: apiData.cta.size
    }
  };
}

/**
 * Maps API response to Events UI model with dictionary translation
 */
export function mapEventsFromApiWithDict(apiData: EventsApiResponse, dict: any): EventsModel {
  const mapped = mapEventsFromApi(apiData);
  
  // Map dictionary keys to actual values
  return {
    title: getDictValue(dict, mapped.title),
    subtitle: getDictValue(dict, mapped.subtitle),
    badge: mapped.badge,
    events: mapped.events.map(event => ({
      id: event.id,
      title: event.title, // Keep event titles as they are (not translated)
      image: {
        src: event.image.src,
        alt: event.image.alt,
        placeholder: event.image.placeholder
      },
      description: event.description, // Keep descriptions as they are (not translated)
      organization: event.organization,
      date: event.date,
      location: event.location,
      category: event.category,
      status: event.status
    })),
    navigation: mapped.navigation,
    layout: mapped.layout,
    cta: {
      text: getDictValue(dict, mapped.cta.text),
      icon: mapped.cta.icon,
      variant: mapped.cta.variant,
      size: mapped.cta.size
    }
  };
}
